import {
    useCallback,
    useMemo,
    useState,
} from 'react';
import { useOpenModal } from '@rainbow-modules/hooks';
import { RenderIf } from 'react-rainbow-components';
import useOpportunity from 'data/firestore/agent/opportunity/use';
import { Todo } from 'data/firestore/agent/todo/types';
import { EntityGet } from 'data/firestore/types';
import useManageTodos from 'data/hooks/useManageTodos';
import getDisplayName from 'data/services/profile/getDisplayName';
import getInitials from 'data/services/string/getInitials';
import { formatRelativeDate } from 'data/services/date/formatter';
import useCustomerPanel from 'hooks/useCustomerPanel';
import { OPPORTUNITY_DETAILS_DRAWER } from '../../../../../constants';
import TodoList from '../../../../../components/OpportunityDetailsDrawer/TodosTab/TodoList';
import TodoCreateForm from '../../../../../components/OpportunityDetailsDrawer/TodosTab/TodoCreateForm';
import {
    OpportunityTodoCard,
    OpportunityHeader,
    OpportunityTitleButton,
    MetaText,
    HeaderTiles,
    HeaderContent,
    CustomerName,
    OwnerTile,
    OwnerAvatar,
    HeaderRow,
    TodosListContainer,
    CustomerAvatar,
} from '../styled';

interface OpportunityTodosCardProps {
    agentId: string;
    selectedPipeline: string;
    opportunityId: string;
    todos: EntityGet<Todo>[];
}

interface TodoUpdatePayload {
    title?: string;
    description?: string;
    status?: 'pending' | 'in_progress' | 'cancelled' | 'done';
    priority?: 'low' | 'medium' | 'high';
    assignedTo?: any | null;
    dueDate?: string | null;
    metadata?: Record<string, unknown>;
}

const OpportunityTodosCardComponent = ({
    agentId,
    selectedPipeline,
    opportunityId,
    todos,
}: OpportunityTodosCardProps) => {
    const [openOpportunityDetailsModal] = useOpenModal(OPPORTUNITY_DETAILS_DRAWER);
    const { openCustomerPanel } = useCustomerPanel();
    const [loadingTodoIds, setLoadingTodoIds] = useState<Set<string>>(new Set());

    const {
        data: opportunity,
        isLoading: isLoadingOpportunity,
    } = useOpportunity(agentId, opportunityId, {
        disabled: !agentId || !opportunityId,
    });

    const { updateTodo, cancelTodo } = useManageTodos({
        agentId,
    });

    const handleStatusToggle = useCallback(
        async (todoId: string, newStatus: string) => {
            setLoadingTodoIds((prev) => new Set(prev).add(todoId));
            try {
                await updateTodo(todoId, { status: newStatus as any });
            } finally {
                setLoadingTodoIds((prev) => {
                    const next = new Set(prev);
                    next.delete(todoId);
                    return next;
                });
            }
        },
        [updateTodo],
    );

    const handleCancel = useCallback(
        async (todoId: string) => {
            setLoadingTodoIds((prev) => new Set(prev).add(todoId));
            try {
                await cancelTodo(todoId);
            } finally {
                setLoadingTodoIds((prev) => {
                    const next = new Set(prev);
                    next.delete(todoId);
                    return next;
                });
            }
        },
        [cancelTodo],
    );

    const handleUpdate = useCallback(
        async (todoId: string, payload: TodoUpdatePayload) => {
            setLoadingTodoIds((prev) => new Set(prev).add(todoId));
            try {
                await updateTodo(todoId, payload);
            } finally {
                setLoadingTodoIds((prev) => {
                    const next = new Set(prev);
                    next.delete(todoId);
                    return next;
                });
            }
        },
        [updateTodo],
    );

    const ownerDisplayName = useMemo(() => {
        if (!opportunity?.owner) return null;
        return getDisplayName(opportunity.owner);
    }, [opportunity?.owner]);

    const ownerInitials = useMemo(
        () => (ownerDisplayName && ownerDisplayName !== 'Unknown' ? getInitials(ownerDisplayName) : null),
        [ownerDisplayName],
    );

    const customerDisplayName = useMemo(
        () => getDisplayName(opportunity?.customer),
        [opportunity?.customer],
    );

    const customerInitials = useMemo(() => {
        if (customerDisplayName === 'Unknown') return null;
        return getInitials(customerDisplayName);
    }, [customerDisplayName]);

    const handleOpenCustomerPanel = useCallback(
        () => openCustomerPanel({ data: opportunity?.customer }),
        [openCustomerPanel, opportunity?.customer],
    );

    const lastUpdatedLabel = useMemo(
        () => (opportunity?.updatedAt ? `Updated ${formatRelativeDate(opportunity.updatedAt)}` : ''),
        [opportunity?.updatedAt],
    );

    if (isLoadingOpportunity) {
        return (
            <OpportunityTodoCard>
                <MetaText>Loading opportunity...</MetaText>
            </OpportunityTodoCard>
        );
    }

    if (!opportunity || opportunity.removed || opportunity.pipelineId !== selectedPipeline) {
        return null;
    }

    return (
        <OpportunityTodoCard>
            <OpportunityHeader>
                <CustomerAvatar
                    size="large"
                    initials={customerInitials}
                    src={opportunity.customer?.photoURL}
                    value={opportunity.customer?.id}
                    channel={opportunity.customer?.channel}
                />
                <HeaderContent>
                    <CustomerName
                        type="button"
                        onClick={handleOpenCustomerPanel}
                    >
                        {customerDisplayName}
                    </CustomerName>
                    <HeaderRow>
                        <OpportunityTitleButton
                            type="button"
                            onClick={() => openOpportunityDetailsModal({ data: opportunity.id })}
                        >
                            {opportunity.name}
                        </OpportunityTitleButton>
                        <MetaText>{lastUpdatedLabel}</MetaText>
                    </HeaderRow>
                    <HeaderTiles>
                        <RenderIf isTrue={Boolean(ownerDisplayName)}>
                            <OwnerTile
                                icon={(
                                    <OwnerAvatar
                                        size="x-small"
                                        initials={ownerInitials}
                                        src={opportunity.owner?.photoURL}
                                        value={opportunity.owner?.uid}
                                    />
                                )}
                                title={ownerDisplayName || ''}
                            />
                        </RenderIf>
                    </HeaderTiles>
                </HeaderContent>
            </OpportunityHeader>
            <TodoCreateForm
                agentId={agentId}
                opportunityId={opportunity.id}
                pipelineId={opportunity.pipelineId}
                stageId={opportunity.stageId}
            />
            <TodosListContainer>
                <TodoList
                    todos={todos}
                    onStatusToggle={handleStatusToggle}
                    onCancel={handleCancel}
                    onUpdate={handleUpdate}
                    loadingTodoIds={loadingTodoIds}
                    agentId={agentId}
                />
            </TodosListContainer>
        </OpportunityTodoCard>
    );
};

export default OpportunityTodosCardComponent;
